import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { TransactionSummary } from '../types';
import { TrendingUp, ShoppingBag, DollarSign, CheckCircle2 } from 'lucide-react';

type DateRange = 'today' | '7d' | '30d';

function getRange(range: DateRange): { from: number; to: number } {
  const now = Date.now();
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const ms = midnight.getTime();
  if (range === 'today') return { from: ms, to: now };
  if (range === '7d') return { from: ms - 6 * 86400000, to: now };
  return { from: ms - 29 * 86400000, to: now };
}

function fmt(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}
function StatCard({ icon, label, value, sub, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-200/60">
      <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center mb-4`}>{icon}</div>
      <p className="text-[13px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-3xl font-extrabold text-slate-900 tracking-tight">{value}</p>
      {sub && <p className="text-sm text-slate-400 font-medium mt-1">{sub}</p>}
    </div>
  );
}

export function Reports() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (range: DateRange) => {
    setLoading(true);
    setError('');
    try {
      const { from, to } = getRange(range);
      const data = await api.getTransactionsSummary({ from, to });
      setSummary(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dateRange); }, [dateRange, load]);

  const rangeButtons: { label: string; value: DateRange }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' },
  ];

  const successRate = summary && summary.transactionCount > 0
    ? Math.round((summary.successCount / summary.transactionCount) * 100)
    : 0;

  const maxRevenue = summary ? Math.max(...summary.daily.map(d => d.revenue), 1) : 1;

  return (
    <div className="max-w-5xl mx-auto pb-24">
      {/* Header */}
      <div className="mb-6 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Reports</h1>
        <p className="text-slate-500 font-medium mt-1">Revenue and sales overview</p>
      </div>

      {/* Date range filter */}
      <div className="flex gap-2 mb-6">
        {rangeButtons.map(b => (
          <button
            key={b.value}
            onClick={() => setDateRange(b.value)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              dateRange === b.value
                ? 'bg-[#2563eb] text-white border-[#2563eb] shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-400 text-base font-medium">Loading…</div>
      ) : summary ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={<DollarSign className="w-6 h-6 text-[#2563eb]" />}
              color="bg-blue-50 border border-blue-100"
              label="Total Revenue"
              value={fmt(summary.totalRevenue)}
              sub="from completed sales"
            />
            <StatCard
              icon={<ShoppingBag className="w-6 h-6 text-emerald-600" />}
              color="bg-emerald-50 border border-emerald-100"
              label="Completed Sales"
              value={String(summary.successCount)}
              sub={`of ${summary.transactionCount} total`}
            />
            <StatCard
              icon={<TrendingUp className="w-6 h-6 text-violet-600" />}
              color="bg-violet-50 border border-violet-100"
              label="Avg Order"
              value={fmt(summary.avgOrderValue)}
              sub="per completed sale"
            />
            <StatCard
              icon={<CheckCircle2 className="w-6 h-6 text-amber-600" />}
              color="bg-amber-50 border border-amber-100"
              label="Success Rate"
              value={`${successRate}%`}
              sub={`${summary.canceledCount + summary.failedCount} canceled/failed`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily breakdown */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-900">Daily Revenue</h2>
                <p className="text-sm text-slate-400 mt-0.5">Completed transactions only</p>
              </div>
              {summary.daily.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm font-medium">
                  No revenue in this period
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {summary.daily.map(row => (
                    <div key={row.date} className="px-6 py-3.5 flex items-center gap-4">
                      <div className="w-24 shrink-0">
                        <p className="text-sm font-bold text-slate-700">{fmtDate(row.date)}</p>
                        <p className="text-xs text-slate-400">{row.count} sale{row.count !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#2563eb] rounded-full"
                          style={{ width: `${Math.round((row.revenue / maxRevenue) * 100)}%` }}
                        />
                      </div>
                      <p className="w-20 text-right text-sm font-bold text-slate-800 shrink-0">{fmt(row.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top products */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-900">Top Products</h2>
                <p className="text-sm text-slate-400 mt-0.5">By revenue, from completed sales</p>
              </div>
              {summary.topProducts.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm font-medium">
                  No product data in this period
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {summary.topProducts.map((p, i) => (
                    <div key={p.name} className="px-6 py-3.5 flex items-center gap-4">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 text-xs font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.quantity} sold</p>
                      </div>
                      <p className="text-sm font-bold text-slate-800 shrink-0">{fmt(p.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status breakdown */}
          {summary.transactionCount > 0 && (
            <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-200/60 mt-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Transaction Breakdown</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Paid', count: summary.successCount, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                  { label: 'Pending', count: summary.pendingCount, color: 'bg-amber-50 text-amber-700 border-amber-200' },
                  { label: 'Failed', count: summary.failedCount, color: 'bg-red-50 text-red-600 border-red-200' },
                  { label: 'Canceled', count: summary.canceledCount, color: 'bg-slate-100 text-slate-500 border-slate-200' },
                ].map(s => (
                  <div key={s.label} className={`rounded-2xl border px-5 py-4 ${s.color}`}>
                    <p className="text-2xl font-extrabold">{s.count}</p>
                    <p className="text-sm font-semibold mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
